import React from 'react';
import { Badge, Box, Flex, Tooltip } from 'pounceV2';
import { useTheme, Theme } from '@mui/material';
import { PRINT_SUPPRESS_ATTR } from 'Components/utils/printAIResponse';
import {
  RiskClassification,
  RiskScoreScaleProps,
  scoreToPercentage,
  PULSE_ANIMATION_SX,
} from './riskScoreScaleUtils';

// Re-export for consumers
export type { RiskClassification, RiskScoreScaleProps };

/**
 * Get classification colors using theme tokens (V3 compliant)
 */
const getClassificationColor = (
  classification: RiskClassification,
  tokens: Theme['palette']['tokens'],
): string => {
  switch (classification) {
    case 'Benign':
      return tokens.background.semantic.success.base;
    case 'Inconclusive':
      return tokens.background.semantic.warning.base;
    case 'Risky':
      return tokens.background.semantic.error.base;
    default:
      return tokens.background.semantic.warning.base;
  }
};

/**
 * Visually clear risk score visualization.
 * Shows a gradient bar with the score marker and key information.
 */
const RiskScoreScaleImpl: React.FC<RiskScoreScaleProps> = ({
  score,
  classification,
  benignThreshold,
  riskyThreshold,
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  const markerPosition = scoreToPercentage(score);
  const color = getClassificationColor(classification, tokens);
  const benignThresholdPosition = scoreToPercentage(benignThreshold);
  const riskyThresholdPosition = scoreToPercentage(riskyThreshold);

  return (
    <Box
      my={2}
      p={3}
      backgroundColor={tokens.background.brand.dark.base}
      borderRadius="medium"
      {...{ [PRINT_SUPPRESS_ATTR]: true }}
    >
      {/* Scale labels above the bar */}
      <Box position="relative" mb={2}>
        <Flex justify="space-between">
          <Badge variant="success-outline" borderStyle="pill">
            Benign
          </Badge>
          <Badge variant="warning-outline" borderStyle="pill">
            Inconclusive
          </Badge>
          <Badge variant="error-outline" borderStyle="pill">
            Risky
          </Badge>
        </Flex>
      </Box>

      {/* Scale visualization */}
      <Box position="relative" height="32px" mb={1}>
        {/* Background gradient bar */}
        <Box
          position="absolute"
          top="6px"
          left={0}
          right={0}
          height="20px"
          borderRadius="small"
          sx={{
            background: `linear-gradient(to right,
                ${tokens.background.semantic.success[1]} 0%,
                ${tokens.background.neutral.mid[3]} 50%,
                ${tokens.background.semantic.warning[1]} 100%)`,
          }}
        />

        {/* Inconclusive range overlay */}
        <Box
          position="absolute"
          top="6px"
          left={`${benignThresholdPosition}%`}
          width={`${riskyThresholdPosition - benignThresholdPosition}%`}
          height="20px"
          backgroundColor={tokens.background.semantic.warning.base}
          opacity={0.4}
          data-testid="inconclusive-range"
        />

        {/* Benign threshold line */}
        <Box
          position="absolute"
          top="0"
          left={`${benignThresholdPosition}%`}
          width="4px"
          height="32px"
          backgroundColor={tokens.background.semantic.warning.base}
          transform="translateX(-50%)"
          data-testid="benign-threshold-line"
        />

        {/* Risky threshold line */}
        <Box
          position="absolute"
          top="0"
          left={`${riskyThresholdPosition}%`}
          width="4px"
          height="32px"
          backgroundColor={tokens.background.semantic.warning.base}
          transform="translateX(-50%)"
          data-testid="risky-threshold-line"
        />

        {/* Score marker */}
        <Tooltip content={`Composite Risk Score: ${score.toFixed(3)}`}>
          <Box
            as="span"
            position="absolute"
            top="0"
            left={`${markerPosition}%`}
            transform="translateX(-50%)"
            width="32px"
            height="32px"
            borderRadius="circle"
            backgroundColor={color}
            border="4px solid"
            borderColor={tokens.background.brand.dark.base}
            boxShadow="0 2px 4px rgba(0,0,0,0.3)"
            cursor="pointer"
            transition="left 0.3s ease-out"
            data-testid="risk-score-marker"
            sx={PULSE_ANIMATION_SX}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};

/**
 * Visual representation of the AI risk score as a balance scale.
 *
 * The scale shows a gradient from Benign (-1) to Risky (+1) with:
 * - A marker indicating the computed Composite Risk Score (CRS)
 * - Color-coded classification (green/yellow/red)
 *
 * The visualization conveys the "weighing of evidence" metaphor used
 * by the risk scoring tool.
 *
 * @example
 * <RiskScoreScaleV3
 *   score={-0.42}
 *   classification="Benign"
 *   benignThreshold={-0.2}
 *   riskyThreshold={0.2}
 * />
 */
const RiskScoreScaleV3 = React.memo(RiskScoreScaleImpl);

export default RiskScoreScaleV3;
